import React, { useCallback, useEffect, useState } from 'react';
import {
  Badge,
  Box,
  CircularProgress,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Chip,
} from '@mui/material';
import { Notifications as NotificationsIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { employeePortalAPI } from '../utils/api';
import type { EmployeeNotification } from '../types/employeePortal';
import { formatActionTypeLabel } from '../utils/formatActionType';

const alertColor = (level?: string | null) => {
  switch (level) {
    case 'red':
      return 'var(--an-error)';
    case 'yellow':
      return 'var(--an-warning)';
    case 'green':
      return 'var(--an-success)';
    default:
      return 'var(--an-primary)';
  }
};

const EmployeeNotificationBell: React.FC = () => {
  const { t } = useTranslation('auth', { keyPrefix: 'employeePortal' });
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notifications, setNotifications] = useState<EmployeeNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [available, setAvailable] = useState(true);

  const fetchNotifications = useCallback(async (): Promise<boolean> => {
    if (!available) return false;
    setLoading(true);
    try {
      const response = await employeePortalAPI.getNotifications({ per_page: 5, unread_only: true });
      if (response.success) {
        setNotifications(response.data.notifications || []);
        setUnreadCount(response.data.unread_count || 0);
      }
      return true;
    } catch (error: any) {
      if (error.response?.status === 404) {
        setAvailable(false);
      }
      return false;
    } finally {
      setLoading(false);
    }
  }, [available]);

  // Polls every 30s while the tab is visible; hidden tabs poll nothing and resume
  // when the tab becomes visible. Failed polls back off exponentially. Unmount
  // (or the portal becoming unavailable) clears the timer and the listener.
  useEffect(() => {
    if (!available) return;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let inFlight = false;
    let consecutiveFailures = 0;

    const scheduleNext = (delay: number) => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(tick, delay);
    };

    const tick = async () => {
      if (cancelled || inFlight) return;
      inFlight = true;
      try {
        if (!document.hidden) {
          const ok = await fetchNotifications();
          if (cancelled) return;
          consecutiveFailures = ok ? 0 : Math.min(consecutiveFailures + 1, 4);
        }
      } finally {
        inFlight = false;
      }
      if (cancelled) return;
      const delay = consecutiveFailures > 0
        ? Math.min(30000 * 2 ** consecutiveFailures, 240000)
        : 30000;
      scheduleNext(delay);
    };

    const handleVisibilityChange = () => {
      if (cancelled || inFlight) return;
      if (document.visibilityState === 'visible') {
        scheduleNext(0);
      }
    };

    tick();
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [available, fetchNotifications]);

  useEffect(() => {
    if (Boolean(anchorEl)) {
      fetchNotifications();
    }
  }, [anchorEl, fetchNotifications]);

  if (!available) return null;

  return (
    <>
      <IconButton
        onClick={(event) => setAnchorEl(event.currentTarget)}
        onMouseEnter={() => fetchNotifications()}
        aria-label={t('notifications')}
        sx={{
          width: 38,
          height: 38,
          color: 'var(--an-primary)',
          backgroundColor: 'var(--an-surface)',
          border: '1px solid var(--an-border-light)',
          borderRadius: 'var(--an-radius-sm)',
          boxShadow: 'var(--an-shadow-sm)',
          '&:hover': {
            backgroundColor: 'var(--an-primary-light)',
          },
        }}
      >
        <Badge badgeContent={unreadCount} color="error" sx={{ '& .MuiBadge-badge': { fontWeight: 800, boxShadow: '0 0 0 2px var(--an-surface)' } }}>
          <NotificationsIcon fontSize="small" />
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          sx: {
            width: { xs: 330, sm: 400 },
            maxWidth: 'calc(100vw - 24px)',
            maxHeight: 500,
            mt: 1,
            overflow: 'hidden',
            border: '1px solid var(--an-border-light)',
            borderRadius: 'var(--an-radius-md)',
            boxShadow: 'var(--an-shadow-lg)',
            backgroundColor: 'var(--an-surface)',
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.75, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, color: 'var(--an-text-primary)' }}>
              {t('notifications')}
            </Typography>
            {unreadCount > 0 && (
              <Typography variant="caption" sx={{ color: 'var(--an-text-secondary)' }}>
                {unreadCount}
              </Typography>
            )}
          </Box>
          {unreadCount > 0 && (
            <Chip size="small" label={unreadCount} color="error" sx={{ fontWeight: 800 }} />
          )}
        </Box>
        <Divider />

        {loading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress size={24} />
          </Box>
        ) : notifications.length === 0 ? (
          <Box sx={{ px: 3, py: 4, textAlign: 'center' }}>
            <Typography sx={{ color: 'var(--an-text-secondary)' }}>{t('noNotifications')}</Typography>
          </Box>
        ) : (
          notifications.map(notification => (
            <MenuItem
              key={notification.id}
              sx={{
                display: 'block',
                whiteSpace: 'normal',
                px: 2,
                py: 1.75,
                borderBottom: '1px solid var(--an-border-light)',
                backgroundColor: 'rgb(var(--an-primary-rgb) / 0.045)',
                '&:hover': {
                  backgroundColor: 'rgb(var(--an-primary-rgb) / 0.09)',
                },
              }}
            >
              <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
                <Box
                  sx={{
                    width: 9,
                    height: 9,
                    mt: 0.65,
                    borderRadius: '50%',
                    backgroundColor: alertColor(notification.alert_level),
                    flexShrink: 0,
                  }}
                />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'var(--an-text-primary)', mb: 0.5 }}>
                    {notification.title || notification.policy_name}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'var(--an-text-secondary)', mb: 0.75 }}>
                    {notification.message}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                    <Chip size="small" label={notification.alert_level || 'info'} variant="outlined" />
                    <Chip size="small" label={formatActionTypeLabel(notification.action_type)} variant="outlined" />
                  </Box>
                </Box>
              </Box>
            </MenuItem>
          ))
        )}

        <Divider />
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            navigate('/employee/notifications');
          }}
          sx={{
            justifyContent: 'center',
            color: 'var(--an-primary)',
            fontWeight: 800,
            py: 1.5,
            '&:hover': {
              backgroundColor: 'var(--an-primary-light)',
            },
          }}
        >
          {t('viewAllNotifications')}
        </MenuItem>
      </Menu>
    </>
  );
};

export default EmployeeNotificationBell;

